// libs/frontend/data-access-auth/src/lib/interceptors/auth.interceptor.ts

import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, skipWhile, switchMap, throwError } from 'rxjs';
import { AuthStore } from './../stores/auth.store';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authStore = inject(AuthStore);
  const injector = inject(Injector);
  const router = inject(Router);

  if (isExcludedFromAuthentication(request)) {
    return next(request);
  }

  request = addAuthenticationToken(request, authStore.jwt() || '');

  return next(request).pipe(
    catchError((err) => {
      if (err.status === 401) {
        if (authStore.refreshToken()) {
          if (!authStore.refreshing()) {
            authStore.refresh();
          }

          return toObservable(authStore.refreshing, {
            injector,
          }).pipe(
            skipWhile((refreshing: boolean) => refreshing),
            switchMap(() => {
              if (authStore.user() && authStore.jwt()) {
                request = addAuthenticationToken(
                  request,
                  authStore.jwt() || ''
                );
                return next(request);
              }

              // ❌ El refresh falló - Limpiar SINCRÓNICAMENTE
              authStore.logout();
              cleanupAndRedirect(router);
              return throwError(() => err);
            })
          );
        }

        // ❌ No hay refresh token - Limpiar SINCRÓNICAMENTE
        authStore.logout();
        cleanupAndRedirect(router);
        return throwError(() => err);
      }

      return throwError(() => err);
    })
  );
};

/**
 * Limpia SINCRONICAMENTE la sesión y redirige al login
 */
function cleanupAndRedirect(router: Router): void {
  // 1. Limpiar TODO sincrónicamente (sin async/await)
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (error: unknown) {
    console.error(error);
    // Silencioso si falla
  }

  // 2. Redirigir al login
  const currentUrl = router.url;

  router.navigate(['/auth/login'], {
    queryParams: {
      sessionExpired: 'true',
      returnUrl: currentUrl !== '/auth/login' ? currentUrl : null
    }
  });
}

function addAuthenticationToken(
  request: HttpRequest<unknown>,
  jwt: string
): HttpRequest<unknown> {
  if (!jwt) {
    return request;
  }
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${jwt}`),
  });
}

function isExcludedFromAuthentication(request: HttpRequest<unknown>): boolean {
  const url = request.url;

  // change-password requiere token - NO excluir
  if (url.includes('auth/change-password')) {
    return false;
  }

  // Excluir assets y endpoints de auth (login, register, forgot-password, refresh-token, etc.)
  // La URL puede ser absoluta: http://localhost:1337/api/auth/local
  const excludedPatterns: RegExp[] = [
    /\/assets\//,  // assets
    /\/auth\//     // auth (login, register, forgot-password, etc.)
  ];

  return excludedPatterns.some((pattern) => pattern.test(url));
}
